/**
 * Studio overview cache. The dashboard summary is the heaviest read in the
 * console and the landing page, so a cold launch paid for it every time. One
 * localStorage entry holds the last successful load (summary + the order-book
 * trend that renders alongside it) stamped with when it was fetched. The page
 * paints from it instantly and only goes back to Xano when the copy is older
 * than the TTL, when the trend range changes, or when staff press Refresh.
 *
 * The data is studio-wide, not per-user, so a single entry is enough — but it
 * is cleared on sign-out so a shared machine never shows figures pre-login.
 * Contract changes to DashboardSummary should bump VERSION; a stale-version
 * entry is ignored (and so re-fetched) rather than rendered.
 */

import type { DashboardSummary, KpiHistoryPoint } from "@/types";

const KEY = "arkitectly.dashboard.cache";
const VERSION = 1;

/** How long a cached load is served without a background refresh. */
export const DASHBOARD_CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

export interface DashboardSnapshot {
  /** Epoch-ms when the fetch completed — what "Updated … ago" shows. */
  savedAt: number;
  /** Trend range the summary was fetched for (the page's own vocabulary). */
  range: string;
  /** trend_months the summary was fetched with; YTD's value moves each month. */
  trendMonths: number;
  summary: DashboardSummary;
  orderBookPoints: KpiHistoryPoint[];
}

type Stored = DashboardSnapshot & { v: number };

export function readDashboardCache(): DashboardSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Stored> | null;
    if (
      !parsed ||
      parsed.v !== VERSION ||
      typeof parsed.savedAt !== "number" ||
      typeof parsed.range !== "string" ||
      typeof parsed.trendMonths !== "number" ||
      !parsed.summary ||
      typeof parsed.summary !== "object"
    ) {
      return null;
    }
    return {
      savedAt: parsed.savedAt,
      range: parsed.range,
      trendMonths: parsed.trendMonths,
      summary: parsed.summary as DashboardSummary,
      orderBookPoints: Array.isArray(parsed.orderBookPoints) ? parsed.orderBookPoints : [],
    };
  } catch {
    return null; // storage unavailable or a corrupt entry — behave as uncached
  }
}

export function writeDashboardCache(snapshot: DashboardSnapshot): void {
  try {
    const stored: Stored = { v: VERSION, ...snapshot };
    localStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* quota / private mode — the page still has the data in memory */
  }
}

export function clearDashboardCache(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/** True once the snapshot is older than the TTL (or stamped in the future,
 *  which means the clock moved — refetch rather than trust it). */
export function isDashboardSnapshotStale(snapshot: DashboardSnapshot, now = Date.now()): boolean {
  const age = now - snapshot.savedAt;
  return age < 0 || age > DASHBOARD_CACHE_TTL_MS;
}
